import React from 'react';
import PropTypes from 'prop-types';
import { getMaterialInputPropsWithDefaults } from 'Helpers/Meta';
import { usePromptHint } from 'Components/UI/Meta/MetaControl/Types/usePromptHint';
import HelperText from '../HelperText';

import { getValue } from '../utils';

export const _stringMetaFieldShowCharacterCount = '_stringMetaFieldShowCharacterCount';
export const _stringMetaFieldShowRemainingCharacters = '_stringMetaFieldShowRemainingCharacters';

function String({
  attrName,
  helperText: propHelperText,
  metaField,
  onChange,
  onInlineSubmit,
  resource,
  CustomProps,
  InputProps,
  SpreadProps,
  HelperProps,
  onFocus,
  onBlur,
  ...rest
}) {
  const Component = metaField.attr('_component');
  const min = metaField.attr('min');
  const max = metaField.attr('max');
  const hint = metaField.attr('hint');
  const { label } = usePromptHint(metaField);
  const readonly = metaField.attr('readonly');

  const value = getValue(resource, metaField, attrName);
  let helperText = propHelperText || '';

  if (metaField.attr(_stringMetaFieldShowCharacterCount)) {
    helperText = `Character count: ${value.length}`;
  }

  if (metaField.attr(_stringMetaFieldShowRemainingCharacters)) {
    const charName = min === 1 ? 'character' : 'characters';

    helperText = value.length < min
      ? `This field must be at least ${min} ${charName}.`
      : `${max - value.length} ${charName} remaining.`;
  }

  let componentInputProps = null;

  if (!metaField.attr('readonly')) {
    componentInputProps = {
      ...componentInputProps,
      ...getMaterialInputPropsWithDefaults(hint, rest),
      ...InputProps,
    };
  }

  return (
    <Component
      helperText={
        <HelperText
          metaField={metaField}
          {...HelperProps}
          helperText={HelperProps.helperText || helperText}
        />
      }
      helperTextValue={HelperProps.helperText || helperText}
      readonly={readonly}
      hidden={metaField.attr('hidden')}
      label={label}
      multiline={metaField.attr('multiline')}
      min={min}
      max={max}
      richtext={metaField.attr('hint') === 'richtext'}
      rows={metaField.attr('rows')}
      rowsMax={metaField.attr('rowsMax')}
      value={value}
      onBlur={(evt) => {
        if (onInlineSubmit) return onInlineSubmit(evt);
        return onBlur && onBlur(evt, attrName);
      }}
      onFocus={(evt) => onFocus && onFocus(evt, attrName)}
      onChange={(evt) => {
        if (evt.target) {
          return onChange(evt.target.value);
        }
        return onChange(evt);
      }}
      required={metaField.attr('required')}
      InputProps={componentInputProps}
      {...SpreadProps}
      {...CustomProps}
      {...rest}
    />
  );
}

String.defaultProps = {
  onInlineSubmit: undefined,
  onBlur: undefined,
  onFocus: undefined,
  HelperProps: {},
};

String.propTypes = {
  HelperProps: PropTypes.object,
  attrName: PropTypes.string.isRequired,
  metaField: PropTypes.object.isRequired,
  onInlineSubmit: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  resource: PropTypes.object.isRequired,
};

export default String;
